/**
 * The delivery a price carries, from the wire (`DeliveryPriceSchema`) to the frontend's `CatalogueDelivery`: the
 * price preview's delivery and each delivery of the staff basket's Delivery section read the same shape.
 */

import type { components } from "./api-schema.generated";
import type { CatalogueDelivery } from "./catalogue-types";

type DeliveryPriceSchema = components["schemas"]["DeliveryPriceSchema"];

/**
 * `?? null`, never `?? 0`: the price is non-null exactly when priced or collected, and a zero would read as free
 * delivery.
 */
export function deliveryFromWire(d: DeliveryPriceSchema): CatalogueDelivery {
  return {
    status: d.status,
    deliveryPricePence: d.delivery_price_pence ?? null,
    source: d.source ?? null,
    serviceLevel: d.service_level ?? null,
    courierName: d.courier_name ?? null,
    courierSupplierId: d.courier_supplier_id ?? null,
    arrivesOn: d.arrives_on ?? null,
    notPricedMessage: d.not_priced_message ?? null,
    notPricedReason: d.not_priced_reason ?? null,
    carriageCostPence: d.carriage_cost_pence ?? null,
    markupPct: d.markup_pct ?? null,
    options: (d.options ?? []).map((o) => ({
      serviceLevel: o.service_level,
      serviceLevelLabel: o.service_level_label,
      courierName: o.courier_name,
      courierSupplierId: o.courier_supplier_id,
      carriageCostPence: o.carriage_cost_pence ?? null,
      deliveryPricePence: o.delivery_price_pence,
      isAvailable: o.is_available,
      unavailableReason: o.unavailable_reason,
      arrivesOn: o.arrives_on,
      isDefault: o.is_default,
    })),
  };
}

/** The inverse of `deliveryFromWire`: a frontend delivery on the wire, as the mocks answer it. */
export function deliveryToWire(d: CatalogueDelivery): DeliveryPriceSchema {
  return {
    status: d.status,
    delivery_price_pence: d.deliveryPricePence,
    source: d.source,
    service_level: d.serviceLevel,
    courier_name: d.courierName,
    courier_supplier_id: d.courierSupplierId,
    arrives_on: d.arrivesOn,
    not_priced_message: d.notPricedMessage,
    not_priced_reason: d.notPricedReason,
    carriage_cost_pence: d.carriageCostPence,
    markup_pct: d.markupPct,
    options: d.options.map((o) => ({
      service_level: o.serviceLevel,
      service_level_label: o.serviceLevelLabel,
      courier_name: o.courierName,
      courier_supplier_id: o.courierSupplierId,
      carriage_cost_pence: o.carriageCostPence,
      delivery_price_pence: o.deliveryPricePence,
      is_available: o.isAvailable,
      unavailable_reason: o.unavailableReason,
      arrives_on: o.arrivesOn,
      is_default: o.isDefault,
    })),
  };
}
